"use client";

import { useEffect, useRef } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useRouter } from "next/navigation";
import { Coffee, Check, ArrowRight, Plus } from "lucide-react";
import { endBreak, skipBreak, extendCurrentPhase } from "@/lib/focus-timer-store";
import { useFocusState, getRemainingMs, formatClock } from "@/components/focus/useFocusState";
import { playBreakEnd } from "@/lib/focus-sounds";

const BREAK_TIPS = [
  "Wstań od biurka, przeciągnij się i odpocznij przez chwilę.",
  "Spójrz w dal przez okno — daj oczom odetchnąć.",
  "Napij się wody i weź kilka głębszych oddechów.",
  "Rozprostuj plecy i rozluźnij ramiona.",
];

export default function BreakScreen() {
  const reduce = useReducedMotion();
  const router = useRouter();
  const state = useFocusState();

  const show = state.mode === "break";
  const remaining = getRemainingMs(state);
  const done = state.breakDone || remaining <= 0;
  const total = state.durationMs || 1;
  const progress = Math.min(1, Math.max(0, (total - remaining) / total));
  // Stabilna podpowiedź na sesję (zależna od id sesji, nie losowana w renderze).
  const tip = BREAK_TIPS[hashCode(state.id) % BREAK_TIPS.length];
  const ret = state.context;

  // Dźwięk powrotu, gdy przerwa dobiegnie końca (raz na sesję).
  const breakEndSoundRef = useRef("");
  useEffect(() => {
    if (show && done && breakEndSoundRef.current !== state.id) {
      breakEndSoundRef.current = state.id;
      playBreakEnd();
    }
  }, [show, done, state.id]);

  const goBack = () => {
    const href = ret?.returnHref;
    endBreak();
    if (href) router.push(href);
  };

  const skip = () => {
    const href = ret?.returnHref;
    skipBreak();
    if (href) router.push(href);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="break-screen"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden bg-gradient-to-br from-indigo-950 via-slate-900 to-slate-950 px-6 text-white"
          style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          {/* Spokojne, subtelne fale w tle */}
          {!reduce && (
            <>
              <motion.div
                className="pointer-events-none absolute -top-24 left-1/2 h-[42rem] w-[42rem] -translate-x-1/2 rounded-full bg-indigo-500/10 blur-3xl"
                animate={{ scale: [1, 1.12, 1], opacity: [0.5, 0.75, 0.5] }}
                transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div
                className="pointer-events-none absolute -bottom-32 left-1/2 h-[36rem] w-[36rem] -translate-x-1/2 rounded-full bg-blue-500/10 blur-3xl"
                animate={{ scale: [1.1, 1, 1.1], opacity: [0.4, 0.65, 0.4] }}
                transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
              />
            </>
          )}

          <div className="relative flex w-full max-w-sm flex-col items-center text-center">
            {!done ? (
              <>
                <motion.div
                  initial={reduce ? undefined : { scale: 0.8, opacity: 0 }}
                  animate={reduce ? undefined : { scale: 1, opacity: 1 }}
                  className="mb-6 flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm font-semibold backdrop-blur-sm"
                >
                  <Coffee size={16} /> Czas na przerwę
                </motion.div>

                {/* Duży timer z oddechowym okręgiem */}
                <div className="relative mb-8 flex items-center justify-center">
                  <svg width={260} height={260} className="-rotate-90">
                    <circle cx={130} cy={130} r={118} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth={6} />
                    <circle
                      cx={130}
                      cy={130}
                      r={118}
                      fill="none"
                      stroke="rgba(255,255,255,0.85)"
                      strokeWidth={6}
                      strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 118}
                      strokeDashoffset={2 * Math.PI * 118 * (1 - progress)}
                      style={{ transition: "stroke-dashoffset 0.95s linear" }}
                    />
                  </svg>
                  <motion.div
                    className="absolute inset-6 rounded-full bg-white/5"
                    animate={reduce ? undefined : { scale: [1, 1.06, 1] }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                  />
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-6xl font-black tabular-nums tracking-tight sm:text-7xl">{formatClock(remaining)}</span>
                    <span className="mt-2 text-sm text-white/60">Czas na chwilę oddechu</span>
                  </div>
                </div>

                <p className="mb-8 max-w-xs text-sm text-white/70">{tip}</p>

                {ret?.subjectLabel && (
                  <div className="mb-4 w-full rounded-2xl bg-white/5 px-4 py-3 text-left backdrop-blur-sm">
                    <p className="text-[11px] uppercase tracking-wide text-white/50">Następnie wrócisz do</p>
                    <p className="mt-0.5 text-sm font-semibold text-white">{ret.subjectLabel}</p>
                  </div>
                )}

                <button
                  onClick={goBack}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-white py-4 text-base font-bold text-slate-900 shadow-lg transition-transform active:scale-[0.98]"
                >
                  <ArrowRight size={18} /> Wróć do nauki
                </button>

                <div className="mt-4 flex items-center gap-5">
                  <button
                    onClick={() => extendCurrentPhase(5)}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-white/70 transition-colors hover:text-white"
                  >
                    <Plus size={14} /> Dłuższa przerwa
                  </button>
                  <button onClick={skip} className="text-sm font-medium text-white/50 transition-colors hover:text-white/80">
                    Pomiń przerwę
                  </button>
                </div>
              </>
            ) : (
              // Przerwa dobiegła końca — delikatne zakończenie.
              <>
                <motion.div
                  initial={reduce ? { opacity: 0 } : { scale: 0, opacity: 0 }}
                  animate={reduce ? { opacity: 1 } : { scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 240, damping: 16 }}
                  className="mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-emerald-500/20"
                >
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg">
                    <Check size={34} strokeWidth={3} />
                  </span>
                </motion.div>
                <h2 className="text-3xl font-black tracking-tight">Gotowy na kolejną sesję?</h2>
                <p className="mt-3 max-w-xs text-sm text-white/70">
                  Przerwa dobiegła końca. Wracamy z nową energią.
                </p>

                {ret?.subjectLabel && (
                  <div className="mt-6 w-full rounded-2xl bg-white/5 px-4 py-3 text-left backdrop-blur-sm">
                    <p className="text-[11px] uppercase tracking-wide text-white/50">Wróć do</p>
                    <p className="mt-0.5 text-sm font-semibold text-white">{ret.subjectLabel}</p>
                  </div>
                )}

                <button
                  onClick={goBack}
                  className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl bg-white py-4 text-base font-bold text-slate-900 shadow-lg transition-transform active:scale-[0.98]"
                >
                  <ArrowRight size={18} /> Wróć do nauki
                </button>
                <button
                  onClick={() => extendCurrentPhase(5)}
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-white/60 transition-colors hover:text-white"
                >
                  <Plus size={14} /> Jeszcze 5 minut przerwy
                </button>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function hashCode(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}
